import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { memberService } from "@/lib/api/members";
import { useAuthStore } from "@/stores/auth";
import { useServiceStore } from "@/stores/service";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

function errorMessageFor(err: unknown): string {
  const status =
    typeof err === "object" && err !== null && "response" in err
      ? (err as { response?: { status?: number } }).response?.status
      : undefined;
  if (status === 410) return "만료되었거나 이미 사용된 초대입니다.";
  if (status === 403) return "이 초대는 다른 계정용입니다.";
  if (status === 409) return "이미 이 서비스의 멤버입니다.";
  return "초대 수락에 실패했습니다.";
}

export function InviteAcceptPage() {
  const { token = "" } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const authToken = useAuthStore((s) => s.token);
  const setSelectedServiceId = useServiceStore((s) => s.setSelectedServiceId);

  useEffect(() => {
    if (!authToken) {
      navigate(`/admin/login?returnTo=${encodeURIComponent(`/admin/invite/${token}`)}`, {
        replace: true,
      });
    }
  }, [authToken, navigate, token]);

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: () => memberService.acceptInvite(token),
    onSuccess: (data) => {
      setSelectedServiceId(data.service_id);
      toast.success(`${data.service_name} 서비스에 참여했습니다.`);
      window.location.assign("/admin");
    },
    onError: (err: unknown) => {
      setErrorMessage(errorMessageFor(err));
    },
  });

  if (!authToken) return null;

  return (
    <div className="bg-atmosphere grain flex min-h-dvh items-center justify-center px-4 py-10">
      <Card className="w-full max-w-sm animate-rise">
        <CardHeader>
          <CardTitle className="font-display text-xl font-medium tracking-tight">
            서비스 초대
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            초대를 수락하면 해당 서비스의 멤버로 등록됩니다.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {errorMessage ? (
            <p className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">
              {errorMessage}
            </p>
          ) : null}
          <Button
            type="button"
            className="w-full"
            disabled={mutation.isPending}
            onClick={() => {
              setErrorMessage(null);
              mutation.mutate();
            }}
          >
            {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            초대 수락
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
